import {announce} from '@github-ui/aria-live'
import {ArrowLeftIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {useEffect, useRef} from 'react'

import styles from './ModelPicker.module.css'

export type AutoModelTier = 'efficiency' | 'balance' | 'intelligence'
export type AutoModelTierState = AutoModelTier | 'fast'
/** A static tier, or an opaque server-defined tier id such as `hydrafusion`. */
export type AutoTierId = AutoModelTier | (string & {})

/** A server-defined tier, e.g. a Copilot Fusion tier from the session's `getAutoTiers` catalog. */
export interface AutoTierOption {
  id: string
  label: string
  description: string
  /** Listed but not selectable; `description` says why. */
  disabled?: boolean
}

export interface AutoTierPickerConfig {
  effectiveTier?: string
  selectedTier?: AutoTierId
  pendingTier?: AutoModelTierState | AutoTierId
  error?: string
  disabled?: boolean
  /** Replaces the static Efficiency/Balance/Intelligence tiers when present. */
  tiers?: readonly AutoTierOption[]
  /** Renames the Auto model, e.g. "Copilot Fusion" while `tiers` are Fusion's. */
  autoModelDisplayName?: string
  onTierSelected: (tier: AutoTierId) => void
}

export type VisibleAutoTierPickerConfig = AutoTierPickerConfig

export const AUTO_MODEL_TIERS = ['efficiency', 'balance', 'intelligence'] as const satisfies readonly AutoModelTier[]

const AUTO_TIER_DETAILS: Record<AutoModelTier, {label: string; description: string}> = {
  efficiency: {
    label: 'Efficiency',
    description: 'Prioritizes speed and efficiency',
  },
  balance: {
    label: 'Balance',
    description: 'Balances speed and intelligence',
  },
  intelligence: {
    label: 'Intelligence',
    description: 'Prioritizes intelligence',
  },
}

export function isVisibleAutoTierPickerConfig(
  config: AutoTierPickerConfig | undefined,
): config is VisibleAutoTierPickerConfig {
  return config !== undefined && config.effectiveTier !== 'fast' && config.pendingTier !== 'fast'
}

export function isAutoModelTier(tier: unknown): tier is AutoModelTier {
  return typeof tier === 'string' && AUTO_MODEL_TIERS.includes(tier as AutoModelTier)
}

export function getAutoTierLabel(tier: AutoModelTier) {
  return AUTO_TIER_DETAILS[tier].label
}

/** The label of `tier` among the config's tiers, or `undefined` when the config doesn't offer it. */
export function getConfiguredAutoTierLabel(
  config: Pick<AutoTierPickerConfig, 'tiers'>,
  tier: string | undefined,
): string | undefined {
  if (tier === undefined) return undefined
  if (config.tiers) return config.tiers.find(option => option.id === tier)?.label
  return isAutoModelTier(tier) ? getAutoTierLabel(tier) : undefined
}

function autoTierOptions(config: Pick<AutoTierPickerConfig, 'tiers'>): readonly AutoTierOption[] {
  return (
    config.tiers ??
    AUTO_MODEL_TIERS.map(tier => ({
      id: tier,
      label: AUTO_TIER_DETAILS[tier].label,
      description: AUTO_TIER_DETAILS[tier].description,
    }))
  )
}

export function getAutoTierModelDisplayNameForTier(modelDisplayName: string, tier: AutoModelTier) {
  return `${modelDisplayName} · ${getAutoTierLabel(tier)}`
}

function getDisplayedAutoTierLabel(config: VisibleAutoTierPickerConfig) {
  return (
    getConfiguredAutoTierLabel(config, config.pendingTier) ??
    getConfiguredAutoTierLabel(config, config.selectedTier) ??
    getConfiguredAutoTierLabel(config, config.effectiveTier)
  )
}

export function getAutoTierModelDisplayName(modelDisplayName: string, config: VisibleAutoTierPickerConfig) {
  const displayedLabel = getDisplayedAutoTierLabel(config)
  return displayedLabel ? `${modelDisplayName} · ${displayedLabel}` : modelDisplayName
}

export function getAutoTierModelAccessibleLabel(modelDisplayName: string, config: VisibleAutoTierPickerConfig) {
  const displayName = getAutoTierModelDisplayName(modelDisplayName, config)
  const pendingLabel = getConfiguredAutoTierLabel(config, config.pendingTier)
  const selectedLabel =
    config.selectedTier && config.selectedTier !== config.effectiveTier
      ? getConfiguredAutoTierLabel(config, config.selectedTier)
      : undefined

  if (pendingLabel) return `Model: ${displayName}, applying optimization`
  if (selectedLabel) return `Model: ${displayName}, selected for the next message`
  return `Model: ${displayName}`
}

export function AutoTierAnnouncements({config}: {config: VisibleAutoTierPickerConfig}) {
  const pendingLabel = getConfiguredAutoTierLabel(config, config.pendingTier)
  const pendingTier = pendingLabel ? config.pendingTier : undefined
  const effectiveLabel = getConfiguredAutoTierLabel(config, config.effectiveTier)
  const previousPendingTierRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    const committed = previousPendingTierRef.current === config.effectiveTier && effectiveLabel !== undefined
    const message =
      config.error ??
      (pendingLabel ? `Applying ${pendingLabel}…` : committed ? `${effectiveLabel} optimization applied.` : undefined)
    previousPendingTierRef.current = pendingTier
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- controlled async status changes must be announced when their props update
    if (message) announce(message)
  }, [config.effectiveTier, config.error, effectiveLabel, pendingLabel, pendingTier])

  return null
}

interface AutoTierMenuProps {
  config: VisibleAutoTierPickerConfig
  onBack: () => void
}

export function AutoTierMenu({config, onBack}: AutoTierMenuProps) {
  const effectiveTier = getConfiguredAutoTierLabel(config, config.effectiveTier) ? config.effectiveTier : undefined
  const pendingLabel = getConfiguredAutoTierLabel(config, config.pendingTier)
  const pendingTier = pendingLabel ? config.pendingTier : undefined
  const selectedTier = pendingTier ?? config.selectedTier ?? effectiveTier
  const selectedLabel =
    !pendingTier && config.selectedTier && config.selectedTier !== effectiveTier
      ? getConfiguredAutoTierLabel(config, config.selectedTier)
      : undefined

  return (
    <>
      <ActionList className={styles.actionList}>
        <ActionList.Item
          onSelect={event => {
            event.preventDefault()
            onBack()
          }}
        >
          <ActionList.LeadingVisual>
            <ArrowLeftIcon />
          </ActionList.LeadingVisual>
          Back to models
        </ActionList.Item>
        <ActionList.Divider />
        <ActionList.Group selectionVariant="single">
          <ActionList.GroupHeading>Optimized for</ActionList.GroupHeading>
          {autoTierOptions(config).map(option => (
            <ActionList.Item
              key={option.id}
              selected={option.id === selectedTier}
              disabled={config.disabled || option.disabled || pendingLabel !== undefined}
              onSelect={() => config.onTierSelected(option.id)}
            >
              {option.label}
              <ActionList.Description variant="block">{option.description}</ActionList.Description>
            </ActionList.Item>
          ))}
        </ActionList.Group>
      </ActionList>
      {pendingLabel || selectedLabel || config.error ? (
        <div className={styles.autoTierMessages}>
          {pendingLabel ? <div className={styles.autoTierStatus}>Applying {pendingLabel}…</div> : null}
          {selectedLabel ? (
            <div className={styles.autoTierStatus}>{selectedLabel} will be used for the next message.</div>
          ) : null}
          {config.error ? <div className={styles.autoTierError}>{config.error}</div> : null}
        </div>
      ) : null}
    </>
  )
}
